/**
 * Timeline Container Component
 * 
 * Container component that manages timeline data and integrates with
 * the events service. Handles loading states, error handling, and
 * data transformation for the timeline display.
 */

import React, { useEffect } from 'react';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import Text from '@amzn/meridian/text';
import Heading from '@amzn/meridian/heading';
import { Timeline } from './timeline';
import { useTimelineData } from '../../../hooks/use-timeline-data';

export interface TimelineContainerProps {
  site?: string;
  /** The ID of the equipment to show events for */
  equipmentId?: string;
  /** Number of events to initially display */
  initialLimit?: number;
  /** Whether to show filters */
  showFilters?: boolean;
  /** Custom title for the timeline */
  title?: string;
  /** Whether to use date range filtering from context */
  useDateRangeFiltering?: boolean;
}

/**
 * Timeline container component that handles data fetching and state management
 */
export const TimelineContainer: React.FC<TimelineContainerProps> = ({
  site,
  equipmentId,
  initialLimit = 10,
  showFilters = true,
  title,
  useDateRangeFiltering = true
}) => {
  const {
    events,
    isLoading,
    error,
    hasMore,
    dateRange,
    fetchEvents,
    loadMore,
    clear
  } = useTimelineData({
    site,
    equipmentId,
    initialLimit,
    autoFetch: true,
    useDateRangeFiltering
  });
  // Clear timeline when equipmentId is undefined
  useEffect(() => {
    if (!site || !equipmentId) {
      clear();
      return;
    }
    fetchEvents(site, equipmentId, initialLimit);
  }, [site, equipmentId, clear]);

  return (
    <Column heights="fill">
      <Timeline
        events={events}
        equipmentId={equipmentId || ''}
        site={site}
        isLoading={isLoading}
        error={error || undefined}
        hasMore={hasMore}
        onLoadMore={loadMore}
        showFilters={showFilters}
        dateRange={dateRange}
        isDateRangeFiltered={!!dateRange}
        noEquipmentSelected={!site || !equipmentId}
      />
    </Column>
  );
};
